/*
 * Copyright 2022 Siemens
 */

import {Subject, type SubjectInterface} from '../shared/observer';
import {Settings} from './settings';

export interface AuthenticationProfile {
  readonly user: {
    readonly display: string;
    readonly name: string;
    readonly id: string;
  };
  readonly role: {
    readonly admin: boolean;
    readonly workspace: boolean;
    readonly token: boolean;
    readonly template: boolean;
  };
  readonly workspace: {
    readonly name: string;
    readonly id: string;
  };
  expiry: Date;
}

export class Authentication {
  static #config: {logout: string; extend: string} | undefined;
  static #profile: AuthenticationProfile | undefined;
  static #refreshRef: NodeJS.Timeout | undefined;

  // NOTE: We observe the Subject when authentication is successful, useful for invalidation ONLY
  static readonly #authenticated = new Subject<boolean | Error>(true);
  static readonly authenticated: SubjectInterface<boolean | Error> = this.#authenticated;

  static init(config: {logout: string; extend: string}, token: string): void {
    this.#config = config;
    this.#handleToken(token);
  }

  static get profile(): AuthenticationProfile | undefined {
    return this.#profile;
  }

  static set profile(profile: AuthenticationProfile | undefined) {
    this.#profile = profile;
    Settings.selectWorkspace(profile?.workspace);
    this.#authenticated.dispatchUnique(profile !== undefined);
  }

  static #handleToken(token: string): void {
    const parsed = this.#parseJWT<{
      d: string;
      exp: number;
      iat: number;
      iss: string;
      n: string;
      r: ReadonlyArray<string>;
      s: string;
      sub: string;
      t: string;
      u: string;
      w: string;
    }>(token);
    if (parsed === undefined) this.#profile = undefined;
    else if (this.#profile === undefined)
      this.#profile = {
        user: {
          display: parsed.d,
          name: parsed.n,
          id: parsed.u,
        },
        role: {
          admin: parsed.r.includes('admin'),
          workspace: parsed.r.includes('workspace'),
          template: parsed.r.includes('template'),
          token: parsed.r.includes('token'),
        },
        workspace: {
          name: parsed.s,
          id: parsed.w,
        },
        expiry: new Date(parsed.exp * 1000),
      };
    else this.#profile.expiry = new Date(parsed.exp * 1000);

    Settings.workspaceNamespace(this.#profile?.user.id);
    Settings.selectWorkspace(Settings.retrieveStoredWorkspace(this.#profile?.workspace));

    this.#authenticated.dispatchUnique(this.#profile !== undefined);
    this.#refreshToken();
  }

  static #refreshToken(): void {
    if (this.#profile === undefined || this.#config === undefined) return;
    if (this.#refreshRef !== undefined) clearTimeout(this.#refreshRef);
    const interval = this.#profile.expiry.getTime() - Date.now() - 30000;
    console.debug('Schedule token refresh at', new Date(this.#profile.expiry.getTime() - 30000).toISOString());
    this.#refreshRef = setTimeout(this.#processRefreshToken, interval);
  }

  static #processRefreshToken(this: void): void {
    // NOTE: 'this: void' allows the usage in setTimeout as-is
    if (Authentication.#config === undefined) return;
    console.debug('Processing token refresh');
    fetch(`${Authentication.#config.extend}?mode=all`, {method: 'GET'})
      .then(
        (auth) => {
          if (
            auth === null ||
            auth.status !== 200 ||
            !auth.headers.has('content-type') ||
            !`${auth.headers.get('content-type')}`.toLowerCase().startsWith('application/json')
          ) {
            return new Error('Failed token renewal');
          }
          return auth.json() as {access_token?: string} | null;
        },
        (err) => {
          return new Error('Invalid token renewal', {cause: err instanceof Error ? err : new Error(err)});
        },
      )
      .then((body) => {
        if (body instanceof Error) Authentication.#authenticated.dispatchUnique(body);
        else if (
          body === null ||
          typeof body !== 'object' ||
          typeof body.access_token !== 'string' ||
          body.access_token.length === 0
        )
          Authentication.#authenticated.dispatchUnique(new Error('Invalid authentication token payload'));
        else Authentication.#handleToken(body.access_token);
      });
  }

  static #parseJWT<T = unknown>(token: string): T | undefined {
    if (typeof (token as unknown) !== 'string' || token.length === 0) return;
    const parts = token.split('.');
    if (parts.length !== 3) return;
    const payload = parts.at(1);
    if (payload === undefined) return;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    try {
      return JSON.parse(
        decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
            .join(''),
        ),
      );
    } catch {}
  }
}
